import React, { useMemo, useState, useRef, useEffect } from "react";
import jsPDF from "jspdf";
import { getAllStudents, refreshStudentsFromFirestore } from "../../services/studentsService";
import { computeStudentFinance, matchLevelConfig, getPaymentModeConfig, computeTotalDue, computeDueAsOfMonth, normalizeCampusKey, parseMoneyAmount } from "../../utils/feeCalculator";
import { getPayments as fetchPayments } from "../../services/transactionsService";
import { db } from "../../services/firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { chartOfAccounts as defaultAccounts } from "../../data/chartOfAccounts";

export default function FinancialReports() {
  const SOA_MONTHS = useMemo(() => ([
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
    "January",
    "February",
    "March"
  ]), []);
  const defaultSoaTargetMonth = (today = new Date()) => {
    const mi = today.getMonth(); // 0=Jan
    if (mi >= 5 && mi <= 11) return SOA_MONTHS[mi - 5]; // Jun..Dec
    if (mi >= 0 && mi <= 2) return SOA_MONTHS[7 + mi]; // Jan..Mar
    return "March"; // Apr/May
  };
  const inferScheduleStartMonth = (schedule) => {
    const lines = Array.isArray(schedule) ? schedule : [];
    for (const s of lines) {
      const label = String(s?.label || s?.period || "").toLowerCase();
      const hit = SOA_MONTHS.find(m => label.includes(m.toLowerCase()));
      if (hit) return hit;
    }
    return "June";
  };
  const ellipsize = (doc, text, maxW) => {
    const raw = String(text || "");
    if (!raw) return "";
    if (doc.getTextWidth(raw) <= maxW) return raw;
    const suffix = "...";
    let t = raw;
    while (t.length > 1 && doc.getTextWidth(t + suffix) > maxW) t = t.slice(0, -1);
    return t + suffix;
  };
  const [timeframe, setTimeframe] = useState("month");
  const [month, setMonth] = useState(String(new Date().getMonth() + 1).padStart(2, "0"));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [campus, setCampus] = useState("");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [filterMode, setFilterMode] = useState("");

  // Source data from Firestore (via service)
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const rows = await fetchPayments();
        setPayments(Array.isArray(rows) ? rows : []);
      } catch {
        setPayments([]);
      }
    })();
  }, []);
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "payments"), (snap) => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPayments(rows);
    }, () => { /* ignore */ });
    return () => unsub();
  }, []);
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "expenses"), (snap) => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setExpenses(rows);
    }, () => { /* ignore */ });
    return () => unsub();
  }, []);

  // Load accounts and build a quick lookup map
  const accounts = useMemo(() => {
    try {
      const raw = localStorage.getItem("accounts");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) && parsed.length ? parsed : defaultAccounts;
    } catch {
      return defaultAccounts;
    }
  }, []);
  const accountMap = useMemo(() => {
    const m = {};
    accounts.forEach(a => { m[a.id] = a; });
    return m;
  }, [accounts]);

  const [students, setStudents] = useState(() => getAllStudents());
  useEffect(() => {
    (async () => {
      try {
        const list = await refreshStudentsFromFirestore();
        if (Array.isArray(list) && list.length) {
          setStudents(list);
        } else {
          setStudents(getAllStudents());
        }
      } catch {
        setStudents(getAllStudents());
      }
    })();
  }, []);
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "students"), (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      try { localStorage.setItem("students_fs_cache", JSON.stringify(list)); } catch (e) { /* ignore */ }
      setStudents(list);
    }, () => { /* ignore */ });
    return () => unsub();
  }, []);
  const studentCampusMap = useMemo(() => {
    const m = new Map();
    (students || []).forEach(s => m.set(String(s.id), String(s.campus || "").toLowerCase()));
    return m;
  }, [students]);
  const studentModeMap = useMemo(() => {
    const m = new Map();
    (students || []).forEach(s => m.set(String(s.id), String(s.paymentMode || "").toLowerCase()));
    return m;
  }, [students]);
  const filtered = useMemo(() => {
    return payments.filter(p => {
      const d = new Date(p.date);
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const y = String(d.getFullYear());
      if (timeframe === "month" && (m !== month || y !== year)) return false;
      if (timeframe === "year" && y !== year) return false;
      if (timeframe === "range") {
        if (rangeStart && d < new Date(rangeStart)) return false;
        if (rangeEnd && d > new Date(rangeEnd)) return false;
      }
      if (campus) {
        const pc = String(p.campus || "").toLowerCase();
        const sc = studentCampusMap.get(String(p.studentId || "")) || "";
        const target = String(campus).toLowerCase();
        if (pc) {
          if (pc !== target) return false;
        } else if (sc !== target) {
          return false;
        }
      }
      if (filterMode) {
        const sm = studentModeMap.get(String(p.studentId || "")) || "";
        if (sm !== String(filterMode).toLowerCase()) return false;
      }
      return true;
    });
  }, [payments, timeframe, month, year, campus, studentCampusMap, rangeStart, rangeEnd, filterMode, studentModeMap]);

  const filteredExpenses = useMemo(() => {
    const toDate = (v) => {
      if (!v) return null;
      if (v instanceof Date) return v;
      if (typeof v?.toDate === "function") return v.toDate();
      const d = new Date(v);
      return Number.isFinite(d.getTime()) ? d : null;
    };
    return expenses.filter(e => {
      const d = toDate(e.date);
      if (!d) return false;
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const y = String(d.getFullYear());
      if (timeframe === "month" && (m !== month || y !== year)) return false;
      if (timeframe === "year" && y !== year) return false;
      if (timeframe === "range") {
        if (rangeStart && d < new Date(rangeStart)) return false;
        if (rangeEnd && d > new Date(rangeEnd)) return false;
      }
      if (campus) {
        const ec = normalizeCampusKey(e.campus);
        const target = normalizeCampusKey(campus);
        if (ec) {
          if (ec !== target) return false;
        } else {
          return false;
        }
      }
      return true;
    });
  }, [expenses, timeframe, month, year, campus, rangeStart, rangeEnd]);

  const totalCollection = useMemo(() => filtered.reduce((s, p) => s + parseMoneyAmount(p.amount), 0), [filtered]);
  const totalExpenses = useMemo(() => filteredExpenses.reduce((s, e) => s + parseMoneyAmount(e.amount), 0), [filteredExpenses]);
  const netRevenue = useMemo(() => totalCollection - totalExpenses, [totalCollection, totalExpenses]);
  const totalCount = useMemo(() => filtered.length, [filtered]);
  const activeCampuses = useMemo(() => {
    const set = new Set();
    filtered.forEach(p => set.add(p.campus || "Unknown"));
    return set.size;
  }, [filtered]);

  const byMethod = useMemo(() => {
    const map = {};
    filtered.forEach(p => { map[p.method] = (map[p.method] || 0) + Number(p.amount || 0); });
    return map;
  }, [filtered]);
  const byCampus = useMemo(() => {
    const map = {};
    filtered.forEach(p => { map[p.campus || "Unknown"] = (map[p.campus || "Unknown"] || 0) + Number(p.amount || 0); });
    return map;
  }, [filtered]);
  const campusStats = useMemo(() => {
    const stats = {};
    filtered.forEach(p => {
      const c = p.campus || "Unknown";
      if (!stats[c]) stats[c] = { total: 0, count: 0 };
      stats[c].total += Number(p.amount || 0);
      stats[c].count += 1;
    });
    return Object.entries(stats).map(([campus, s]) => ({ campus, total: s.total, count: s.count }));
  }, [filtered]);
  const byAccount = useMemo(() => {
    const map = {};
    filtered.forEach(p => {
      const name = p.accountId ? (accountMap[p.accountId]?.name || "Unclassified") : "Unclassified";
      map[name] = (map[name] || 0) + Number(p.amount || 0);
    });
    return map;
  }, [filtered, accountMap]);

  const campusTableRef = useRef(null);
  const dailyReportRef = useRef(null);

  // Daily financial reports data grouped by campus and date
  const dailyReports = useMemo(() => {
    if (timeframe !== "month") return {};
    
    const campusData = {};
    const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
    
    // Initialize all campuses and dates
    const allCampuses = [...new Set(filtered.map(p => p.campus || "Unknown"))];
    allCampuses.forEach(campus => {
      campusData[campus] = {};
      for (let day = 1; day <= daysInMonth; day++) {
        campusData[campus][day] = { amount: 0, count: 0 };
      }
    });
    
    // Populate with actual data
    filtered.forEach(payment => {
      const campus = payment.campus || "Unknown";
      const date = new Date(payment.date);
      const day = date.getDate();
      
      if (campusData[campus] && campusData[campus][day]) {
        campusData[campus][day].amount += Number(payment.amount || 0);
        campusData[campus][day].count += 1;
      }
    });
    
    return campusData;
  }, [filtered, timeframe, year, month]);

  // Calculate grand totals for daily reports
  const dailyGrandTotals = useMemo(() => {
    if (timeframe !== "month") return {};
    
    const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
    const totals = {};
    
    for (let day = 1; day <= daysInMonth; day++) {
      totals[day] = { amount: 0, count: 0 };
      Object.values(dailyReports).forEach(campusData => {
        if (campusData[day]) {
          totals[day].amount += campusData[day].amount;
          totals[day].count += campusData[day].count;
        }
      });
    }
    
    return totals;
  }, [dailyReports, timeframe, year, month]);

  const exportCampusCSV = () => {
    const header = ["Campus", "Total Collection", "Payment Count"];
    const rows = campusStats.map(r => [r.campus, r.total, r.count]);
    const csv = [header.join(","), ...rows.map(row => row.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const suffix = timeframe === "month" ? `${year}-${month}` : `${year}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `campus-report_${suffix}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const exportMethodCSV = () => {
    const header = ["Method", "Amount"];
    const rows = Object.entries(byMethod).map(([method, amt]) => [method, Number(amt)]);
    const csv = [header.join(","), ...rows.map(row => row.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const suffix =
      timeframe === "month" ? `${year}-${month}` :
      timeframe === "year" ? `${year}` :
      `${rangeStart || "start"}_${rangeEnd || "end"}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `method-report_${suffix}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportDailyReportCSV = () => {
    if (timeframe !== "month") return;
    
    const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
    const campuses = Object.keys(dailyReports);
    
    // Create header
    const header = ["Day", ...campuses.flatMap(c => [`${c} Amount`, `${c} Count`]), "Total Amount", "Total Count"];
    
    // Create rows
    const rows = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const row = [day];
      campuses.forEach(campus => {
        const data = dailyReports[campus][day] || { amount: 0, count: 0 };
        row.push(data.amount, data.count);
      });
      const totals = dailyGrandTotals[day] || { amount: 0, count: 0 };
      row.push(totals.amount, totals.count);
      rows.push(row);
    }
    
    const csv = [header.join(","), ...rows.map(row => row.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const suffix = timeframe === "month" ? `${year}-${month}` : `${year}`;
    const a = document.createElement("a");
    // Only declare printWindow once
    const printWindow = window.open("", "PRINT", "height=800,width=1200");
    printWindow.document.write("<link rel=\"stylesheet\" href=\"https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css\" />");
    printWindow.document.write("</head><body>");
    printWindow.document.write("<div class=\"container-fluid\">");
    printWindow.document.write(dailyReportRef.current?.outerHTML || "");
    printWindow.document.write("</div></body></html>");
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
  const [soaStudentId, setSoaStudentId] = useState("");
  const [soaStudentSearch, setSoaStudentSearch] = useState("");

  const paymentsByStudent = useMemo(() => {
    const map = {};
    payments.forEach(p => {
      const sid = String(p.studentId || "");
      if (!sid) return;
      if (!map[sid]) map[sid] = [];
      map[sid].push(p);
    });
    return map;
  }, [payments]);
  const campusFinance = useMemo(() => {
    const totals = { tuition: 0, paid: 0, balance: 0, students: 0 };
    students.forEach(s => {
      if (campus && String(s.campus || "").toLowerCase() !== String(campus).toLowerCase()) return;
      const studentPayments = paymentsByStudent[String(s.id)] || [];
      const mode = s.paymentMode || "Annual";
      const fin = computeStudentFinance({ ...s, paymentMode: mode }, studentPayments, "Annual");
      const totalDue = Number(fin.totalDue || computeTotalDue(getPaymentModeConfig(matchLevelConfig(s.campus, s.gradeLevel), mode)) || 0);
      const totalPaid = studentPayments.reduce((t,p)=>t+Number(p.amount||0),0);
      const balance = Math.max(0, totalDue - totalPaid);
      totals.tuition += totalDue;
      totals.paid += totalPaid;
      totals.balance += balance;
      totals.students += 1;
    });
    return totals;
  }, [students, paymentsByStudent, campus]);

  const exportSOA = () => {
    if (!soaStudentId) return;
    const student = students.find(s => String(s.id) === String(soaStudentId));
    if (!student) {
      alert("Student ID not found.");
      return;
    }
    const studentName = [student.firstname, student.middlename, student.lastname].filter(Boolean).join(" ").trim() || student.fullname || "";
    const studentCampus = student.campus || "";
    const studentGradeLevel = student.gradeLevel || "";
    const campusKey = normalizeCampusKey(studentCampus);
    const preparedBy = campusKey === "bulacan" ? "Prepared by: Emerenciana L. Ventura" : "Prepared by: Catalina T. Basa";

    const allPays = paymentsByStudent[String(student.id)] || [];
    const totalPaid = allPays.reduce((s, p) => s + parseMoneyAmount(p.amount), 0);
    const mode = student.paymentMode || "Installment";
    const fin = computeStudentFinance({ ...student, campus: campusKey, paymentMode: mode }, allPays, "Annual");
    const totalDue = Number(fin.totalDue || 0);
    const outstanding = Math.max(0, totalDue - totalPaid);

    const startYear = Number(student.enrollmentYear);
    const startMonth = inferScheduleStartMonth(fin.schedule || []);
    const dueInfo = computeDueAsOfMonth(fin.schedule || [], allPays, soaTargetMonth, { startMonth, startYear: Number.isFinite(startYear) ? startYear : undefined });
    const totalAmountDue = Number(dueInfo.dueAmount || 0);
    const dueDate = String(dueInfo.dueDate || "");
    const FOLIO = [612, 936]; // 8.5 x 13 in points
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: FOLIO });
    const money = (n) => `P${Number(n || 0).toLocaleString()}`;
    const toDate = (v) => {
      if (!v) return null;
      if (v instanceof Date) return v;
      if (typeof v?.toDate === "function") return v.toDate();
      const d = new Date(v);
      return Number.isFinite(d.getTime()) ? d : null;
    };
    const ymd = (d) => {
      const dt = toDate(d);
      if (!dt) return "";
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    };
    const monthLabel = (d) => {
      const dt = toDate(d);
      if (!dt) return "";
      const names = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      return `${names[dt.getMonth()]} ${dt.getFullYear()}`;
    };
    const asOf = ymd(new Date());
    const renderSoaCard = (x, y, w, h) => {
      const pad = 14;
      const maxTextW = w - pad * 2;
      doc.setDrawColor(220);
      doc.rect(x, y, w, h, "S");

      doc.setTextColor(0);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("Village Montessori School and Colleges", x + w / 2, y + 34, { align: "center" });
      doc.setFontSize(11);
      doc.text("STATEMENT OF ACCOUNT", x + w / 2, y + 54, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.text(`As of ${asOf}`, x + w / 2, y + 72, { align: "center" });

      let cy = y + 110;
      const leftX = x + pad;
      doc.setFontSize(10);
      doc.setTextColor(0);

      doc.setFont("helvetica", "bold");
      doc.text("Student:", leftX, cy);
      doc.setFont("helvetica", "normal");
      doc.text(ellipsize(doc, ` ${studentName || "-"}`, maxTextW - 60), leftX + 52, cy);
      cy += 22;

      doc.setFont("helvetica", "bold");
      doc.text("ID:", leftX, cy);
      doc.setFont("helvetica", "normal");
      doc.text(ellipsize(doc, ` ${student.id || "-"}`, maxTextW - 30), leftX + 18, cy);
      cy += 22;

      doc.setFont("helvetica", "bold");
      doc.text("Grade Level:", leftX, cy);
      doc.setFont("helvetica", "normal");
      doc.text(ellipsize(doc, ` ${studentGradeLevel || "-"}`, maxTextW - 85), leftX + 74, cy);
      cy += 22;

      doc.setFont("helvetica", "bold");
      doc.text("Campus:", leftX, cy);
      doc.setFont("helvetica", "normal");
      doc.text(ellipsize(doc, ` ${studentCampus || "-"}`, maxTextW - 55), leftX + 54, cy);

      const tilesY = y + 214;
      const gap = 16;
      const tileW = (w - pad * 2 - gap * 3) / 4;
      const tileH = 56;
      const tileX0 = x + pad;
      const drawTile = (tx, label, value, color = [0, 0, 0]) => {
        doc.setDrawColor(200);
        doc.setFillColor(255, 255, 255);
        doc.rect(tx, tilesY, tileW, tileH, "S");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(60);
        doc.text(label, tx + tileW / 2, tilesY + 18, { align: "center" });
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(color[0], color[1], color[2]);
        doc.text(value, tx + tileW / 2, tilesY + 40, { align: "center" });
      };

      drawTile(tileX0 + (tileW + gap) * 0, "Total Due", money(totalDue), [0, 0, 0]);
      drawTile(tileX0 + (tileW + gap) * 1, "Total Paid", money(totalPaid), [0, 0, 0]);
      drawTile(tileX0 + (tileW + gap) * 2, "Outstanding Balance", money(outstanding), [25, 118, 210]);
      drawTile(tileX0 + (tileW + gap) * 3, "Total Amount Due", money(totalAmountDue), [198, 40, 40]);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.text(`Due Date: ${dueDate || "-"}`, x + w / 2, tilesY + tileH + 36, { align: "center" });

      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(60);
      doc.text("Kindly settle the following account on or before the assigned date.", x + w / 2, tilesY + tileH + 56, { align: "center" });

      const tableX = x + pad;
      const tableW = w - pad * 2;
      let ty = tilesY + tileH + 92;
      const bottomLimit = y + h - 190;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0);
      doc.text("Payment History", tableX, ty);
      ty += 18;

      const colDate = tableX;
      const colDesc = tableX + 120;
      const colRef = tableX + 300;
      const colMethod = tableX + 410;
      const colAmount = tableX + tableW;

      const lineY = (yy) => {
        doc.setDrawColor(120);
        doc.setLineWidth(0.6);
        doc.line(tableX, yy, tableX + tableW, yy);
      };

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Date", colDate, ty);
      doc.text("Description", colDesc, ty);
      doc.text("Reference", colRef, ty);
      doc.text("Method", colMethod, ty);
      doc.text("Amount", colAmount, ty, { align: "right" });
      ty += 8;
      lineY(ty);
      ty += 16;

      const rows = (allPays || [])
        .map(p => {
          const d = toDate(p.date);
          return {
            d,
            date: ymd(p.date || ""),
            month: monthLabel(p.date || ""),
            monthKey: d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` : "",
            method: String(p.method || ""),
            reference: String(p.reference || ""),
            amount: parseMoneyAmount(p.amount)
          };
        })
        .filter(r => r.d)
        .sort((a, b) => a.d - b.d);

      if (rows.length === 0) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(80);
        doc.text("No payments found.", tableX, ty);
        ty += 16;
      } else {
        let currentMonthKey = "";
        let truncated = false;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(0);

        for (const r of rows) {
          if (ty + 18 > bottomLimit) {
            truncated = true;
            break;
          }
          if (r.monthKey !== currentMonthKey) {
            currentMonthKey = r.monthKey;
            doc.setFont("helvetica", "bold");
            doc.text(r.month || "-", colDate, ty);
            doc.setFont("helvetica", "normal");
            ty += 16;
            lineY(ty - 8);
            if (ty + 18 > bottomLimit) {
              truncated = true;
              break;
            }
          }
          doc.text(r.date || "-", colDate, ty);
          doc.text(ellipsize(doc, "Payment", 170), colDesc, ty);
          doc.text(ellipsize(doc, r.reference || "-", 90), colRef, ty);
          doc.text(ellipsize(doc, r.method || "-", 70), colMethod, ty);
          doc.text(money(r.amount), colAmount, ty, { align: "right" });
          ty += 16;
        }

        if (truncated) {
          doc.setFont("helvetica", "italic");
          doc.setFontSize(8.5);
          doc.setTextColor(90);
          doc.text("Some payments are not shown due to page space.", tableX, bottomLimit);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(0);
          ty = Math.max(ty, bottomLimit + 14);
        }
      }

      if (ty + 22 <= y + h - 170) {
        lineY(ty - 8);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(0);
        doc.text("Total Payments:", colMethod, ty + 10, { align: "right" });
        doc.text(money(totalPaid), colAmount, ty + 10, { align: "right" });
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.text(preparedBy, x + w - pad, y + h - 150, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(80);
      doc.text(`This is an official statement of account as of ${asOf}.`, x + w / 2, y + h - 80, { align: "center" });
      doc.text("For questions or concerns, please contact the school finance office.", x + w / 2, y + h - 62, { align: "center" });
    };

    const margin = 24;
    renderSoaCard(margin, margin, 612 - margin * 2, 936 - margin * 2);
    doc.save(`SOA_${student.id}.pdf`);
  };

  const [batchCampus, setBatchCampus] = useState("");
  const [batchMode, setBatchMode] = useState("Installment");
  const [soaTargetMonth, setSoaTargetMonth] = useState(() => defaultSoaTargetMonth());
  const [batchTargetMonth, setBatchTargetMonth] = useState(() => defaultSoaTargetMonth());
  const [previewTab, setPreviewTab] = useState(true);
  const normalizeCampus = (c) => {
    const s = String(c || "").toLowerCase();
    if (s.includes("gapan")) return "gapan";
    if (s.includes("bulacan")) return "bulacan";
    return s;
  };
  const matchedCount = useMemo(() => {
    const targetCampus = normalizeCampus(batchCampus);
    return students.filter(s => {
      if (targetCampus && normalizeCampus(s.campus) !== targetCampus) return false;
      const mode = String(s.paymentMode || "").toLowerCase();
      return mode === String(batchMode).toLowerCase();
    }).length;
  }, [students, batchCampus, batchMode]);
  const exportBatchSOAInstallment = () => {
    const campusFilter = normalizeCampus(batchCampus);
    const candidates = students.filter(s => {
      if (campusFilter && normalizeCampus(s.campus) !== campusFilter) return false;
      const mode = String(s.paymentMode || "").toLowerCase();
      if (String(batchMode).toLowerCase() === "all modes") return true;
      return mode === String(batchMode).toLowerCase();
    });
    if (candidates.length === 0) {
      alert("No students matched the selected campus and payment mode.");
      return;
    }
    const paymentsById = paymentsByStudent;
    const FOLIO = [612, 936]; // 8.5 x 13 in points
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: FOLIO });
    const money = (n) => `P${Number(n || 0).toLocaleString()}`;
    const toDate = (v) => {
      if (!v) return null;
      if (v instanceof Date) return v;
      if (typeof v?.toDate === "function") return v.toDate();
      const d = new Date(v);
      return Number.isFinite(d.getTime()) ? d : null;
    };
    const ymd = (d) => {
      const dt = toDate(d);
      if (!dt) return "";
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    };
    const pageW = 612; // points
    const pageH = 936;
    const marginX = 24;
    const desiredPerPage = candidates.length >= 3 ? 3 : candidates.length || 1;
    const minCardH = 310;
    const perPage = Math.min(desiredPerPage, Math.max(1, Math.floor((pageH - marginX * 2) / minCardH)));
    const cardH = Math.floor((pageH - marginX * 2) / perPage);
    const cardW = pageW - marginX * 2;
    const officer = (camp) => {
      const c = normalizeCampusKey(camp);
      if (c === "bulacan") return "Prepared by: Emerenciana L. Ventura";
      return "Prepared by: Catalina T. Basa";
    };
    let y = marginX;
    let idx = 0;
    const renderCard = (x, y0, w, h, data) => {
      const pad = 12;
      const maxTextW = w - pad * 2;
      doc.setDrawColor(200);
      doc.rect(x, y0, w, h, "S");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0);
      doc.text("STATEMENT OF ACCOUNT", x + w / 2, y0 + 22, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.text(`As of ${data.asOf}`, x + w / 2, y0 + 36, { align: "center" });

      let cy = y0 + 60;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Student:", x + pad, cy);
      doc.setFont("helvetica", "normal");
      doc.text(ellipsize(doc, ` ${data.name || "-"}`, maxTextW - 55), x + pad + 50, cy);
      cy += 14;

      doc.setFont("helvetica", "bold");
      doc.text("ID:", x + pad, cy);
      doc.setFont("helvetica", "normal");
      doc.text(ellipsize(doc, ` ${data.studentId || "-"}`, maxTextW - 20), x + pad + 16, cy);
      cy += 14;

      doc.setFont("helvetica", "bold");
      doc.text("Grade Level:", x + pad, cy);
      doc.setFont("helvetica", "normal");
      doc.text(ellipsize(doc, ` ${data.gradeLevel || "-"}`, maxTextW - 75), x + pad + 70, cy);
      cy += 14;

      doc.setFont("helvetica", "bold");
      doc.text("Campus:", x + pad, cy);
      doc.setFont("helvetica", "normal");
      doc.text(ellipsize(doc, ` ${data.campus || "-"}`, maxTextW - 55), x + pad + 52, cy);

      const tilesY = y0 + 118;
      const gap = 10;
      const tileW = (w - pad * 2 - gap * 3) / 4;
      const tileH = 44;
      const tileX0 = x + pad;
      const drawTile = (tx, label, value, color = [0, 0, 0]) => {
        doc.setDrawColor(200);
        doc.rect(tx, tilesY, tileW, tileH, "S");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.8);
        doc.setTextColor(60);
        doc.text(label, tx + tileW / 2, tilesY + 14, { align: "center" });
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9.8);
        doc.setTextColor(color[0], color[1], color[2]);
        doc.text(value, tx + tileW / 2, tilesY + 32, { align: "center" });
      };
      drawTile(tileX0 + (tileW + gap) * 0, "Total Due", money(data.totalDue), [0, 0, 0]);
      drawTile(tileX0 + (tileW + gap) * 1, "Total Paid", money(data.totalPaid), [0, 0, 0]);
      drawTile(tileX0 + (tileW + gap) * 2, "Outstanding Balance", money(data.outstanding), [25, 118, 210]);
      drawTile(tileX0 + (tileW + gap) * 3, "Total Amount Due", money(data.totalAmountDue), [198, 40, 40]);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(0);
      doc.text(`Due Date: ${data.dueDate || "-"}`, x + w / 2, tilesY + tileH + 18, { align: "center" });

      const historyY = tilesY + tileH + 40;
      const maxLines = h < 360 ? 2 : 3;
      const rows = (data.payments || [])
        .map(p => {
          const dt = toDate(p.date);
          return {
            d: dt,
            date: ymd(p.date || ""),
            method: String(p.method || ""),
            reference: String(p.reference || ""),
            amount: parseMoneyAmount(p.amount)
          };
        })
        .filter(r => r.d)
        .sort((a, b) => b.d - a.d)
        .slice(0, maxLines);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(0);
      doc.text("Payment History", x + pad, historyY);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(60);
      if (rows.length === 0) {
        doc.text("No payments found.", x + pad, historyY + 14);
      } else {
        let ly = historyY + 14;
        for (const r of rows) {
          const line = `${r.date || "-"} • ${r.method || "-"} • ${r.reference || "-"} • ${money(r.amount)}`;
          doc.text(ellipsize(doc, line, maxTextW), x + pad, ly);
          ly += 12;
        }
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(0);
      doc.text(data.preparedBy, x + w - pad, y0 + h - 12, { align: "right" });
    };
    candidates.forEach(s => {
      const studentPayments = paymentsById[String(s.id)] || [];
      const mode = s.paymentMode || "Installment";
      const fin = computeStudentFinance({ ...s, campus: normalizeCampusKey(s.campus), paymentMode: mode }, studentPayments, "Annual");
      const totalDue = Number(fin.totalDue || 0);
      const totalPaid = studentPayments.reduce((t,p)=>t+parseMoneyAmount(p.amount),0);
      const balance = Math.max(0, totalDue - totalPaid);
      const startYear = Number(s.enrollmentYear);
      const startMonth = inferScheduleStartMonth(fin.schedule || []);
      const dueInfo = computeDueAsOfMonth(fin.schedule || [], studentPayments, batchTargetMonth, { startMonth, startYear: Number.isFinite(startYear) ? startYear : undefined });
      const dueAmount = Number(dueInfo.dueAmount || 0);
      const dueDate = String(dueInfo.dueDate || "");
      const name = [s.firstname, s.middlename, s.lastname].filter(Boolean).join(" ").trim() || s.fullname || "";
      const asOf = (() => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${dd}`;
      })();
      renderCard(marginX, y, cardW, cardH, {
        name,
        studentId: s.id,
        campus: s.campus,
        gradeLevel: s.gradeLevel || "",
        totalDue,
        totalPaid,
        outstanding: balance,
        totalAmountDue: dueAmount,
        dueDate,
        preparedBy: officer(s.campus),
        asOf,
        payments: studentPayments
      });
      // next card
      idx += 1;
      if (idx % perPage === 0 && idx < candidates.length) {
        doc.addPage();
        y = marginX;
      } else {
        y += cardH;
      }
    });
    const suffix = batchCampus ? String(batchCampus) : "All";
    const filename = `Batch_SOA_${suffix}.pdf`;
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    if (previewTab) window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  return (
    <div className="container mt-4">
      <h1>Financial Reports</h1>
      {/* Summary Cards */}
      <div className="row g-3 mb-3">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm p-3" style={{background:"#e3f2fd"}}>
            <div className="text-muted">Total Collection ({timeframe === "month" ? `${year}-${month}` : year})</div>
            <div className="display-6 fw-bold text-primary">₱{totalCollection.toLocaleString()}</div>
            <div className="small text-muted">Transactions: {totalCount} • Campuses: {activeCampuses}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm p-3" style={{background:"#ffebee"}}>
            <div className="text-muted">Total Expenses ({timeframe === "month" ? `${year}-${month}` : year})</div>
            <div className="display-6 fw-bold text-danger">₱{totalExpenses.toLocaleString()}</div>
            <div className="small text-muted">Entries: {filteredExpenses.length}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm p-3" style={{background:"#e8f5e9"}}>
            <div className="text-muted">Net Revenue ({timeframe === "month" ? `${year}-${month}` : year})</div>
            <div className="display-6 fw-bold text-success">₱{netRevenue.toLocaleString()}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm p-3" style={{background:"#fffde7"}}>
            <div className="text-muted">Total Tuition (All Students{campus ? ` • ${campus}` : ""})</div>
            <div className="display-6 fw-bold text-warning">₱{campusFinance.tuition.toLocaleString()}</div>
            <div className="small text-muted">{campusFinance.students} student(s)</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm p-3" style={{background:"#e8f5e9"}}>
            <div className="text-muted">Total Paid (All Time{campus ? ` • ${campus}` : ""})</div>
            <div className="display-6 fw-bold text-success">₱{campusFinance.paid.toLocaleString()}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm p-3" style={{background:"#ffebee"}}>
            <div className="text-muted">Outstanding Balance (All Students{campus ? ` • ${campus}` : ""})</div>
            <div className="display-6 fw-bold text-danger">₱{campusFinance.balance.toLocaleString()}</div>
          </div>
        </div>
      </div>
      <div className="card p-3 mb-3">
        <h5 className="mb-2">Statement of Account (SOA)</h5>
        <div className="row g-2 align-items-end">
          <div className="col-md-5">
            <label className="form-label">Student Name</label>
            <input
              className="form-control"
              list="soa-student-list"
              value={soaStudentSearch}
              onChange={e => {
                const v = e.target.value;
                setSoaStudentSearch(v);
                const val = String(v || "").trim();
                const token = val.includes("—") ? val.split("—").pop() : val;
                const clean = String(token || "").trim();
                const byId = (students || []).find(s => String(s.id || "").toLowerCase() === clean.toLowerCase());
                if (byId) {
                  setSoaStudentId(String(byId.id));
                  return;
                }
                const byName = (students || []).find(s => {
                  const n = ([s.firstname, s.middlename, s.lastname].filter(Boolean).join(" ").trim() || s.fullname || "").toLowerCase();
                  return n === clean.toLowerCase();
                });
                if (byName) {
                  setSoaStudentId(String(byName.id));
                  return;
                }
                setSoaStudentId("");
              }}
              placeholder="Type student name (or ID)"
            />
            <datalist id="soa-student-list">
              {(students || [])
                .slice()
                .sort((a, b) => {
                  const an = ([a.firstname, a.middlename, a.lastname].filter(Boolean).join(" ").trim() || a.fullname || "").toLowerCase();
                  const bn = ([b.firstname, b.middlename, b.lastname].filter(Boolean).join(" ").trim() || b.fullname || "").toLowerCase();
                  return an.localeCompare(bn);
                })
                .map(s => {
                  const n = [s.firstname, s.middlename, s.lastname].filter(Boolean).join(" ").trim() || s.fullname || "";
                  return <option key={String(s.id)} value={`${n} — ${s.id}`} />;
                })}
            </datalist>
            <div className="small text-muted mt-1">Selected ID: {soaStudentId || "-"}</div>
          </div>
          <div className="col-md-3">
            <label className="form-label">Target Month</label>
            <select className="form-select" value={soaTargetMonth} onChange={e=>setSoaTargetMonth(e.target.value)}>
              {SOA_MONTHS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <button className="btn btn-primary" onClick={exportSOA}>Export SOA (PDF)</button>
          </div>
        </div>
        <div className="row g-2 mt-3 align-items-end">
          <div className="col-md-3">
            <label className="form-label">Batch Campus</label>
            <select className="form-select" value={batchCampus} onChange={e=>setBatchCampus(e.target.value)}>
              <option value="">All</option>
              <option value="gapan">Gapan</option>
              <option value="bulacan">Bulacan</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Batch Payment Mode</label>
            <select className="form-select" value={batchMode} onChange={e=>setBatchMode(e.target.value)}>
              <option>All Modes</option>
              <option>Installment</option>
              <option>Semestral</option>
              <option>Annual</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Target Month</label>
            <select className="form-select" value={batchTargetMonth} onChange={e=>setBatchTargetMonth(e.target.value)}>
              {SOA_MONTHS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Batch SOA</label>
            <button className="btn btn-outline-primary w-100" onClick={exportBatchSOAInstallment} disabled={matchedCount === 0}>
              Generate Batch SOA — {batchMode} (up to 3 per 8.5×13)
            </button>
            <div className="small text-muted mt-1">Matched: {matchedCount} student(s)</div>
          </div>
          <div className="col-md-3">
            <label className="form-label">Preview</label>
            <div className="form-check">
              <input className="form-check-input" type="checkbox" id="previewTab" checked={previewTab} onChange={e=>setPreviewTab(e.target.checked)} />
              <label className="form-check-label" htmlFor="previewTab">Open PDF in new tab</label>
            </div>
          </div>
        </div>
      </div>
      {/* Collections by Method */}
      <div className="card p-3 mb-3">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-2">Collections by Payment Method</h5>
          <div className="d-flex gap-2">
            <select className="form-select" style={{width:160}} value={timeframe} onChange={e=>setTimeframe(e.target.value)}>
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
              <option value="range">Date Range</option>
            </select>
            {timeframe === "month" && (
              <>
                <input className="form-control" style={{width:100}} value={month} onChange={e=>setMonth(e.target.value)} placeholder="MM" />
                <input className="form-control" style={{width:120}} value={year} onChange={e=>setYear(e.target.value)} placeholder="YYYY" />
              </>
            )}
            {timeframe === "year" && (
              <input className="form-control" style={{width:120}} value={year} onChange={e=>setYear(e.target.value)} placeholder="YYYY" />
            )}
            {timeframe === "range" && (
              <>
                <input type="date" className="form-control" style={{width:160}} value={rangeStart} onChange={e=>setRangeStart(e.target.value)} />
                <input type="date" className="form-control" style={{width:160}} value={rangeEnd} onChange={e=>setRangeEnd(e.target.value)} />
              </>
            )}
            <select className="form-select" style={{width:160}} value={campus} onChange={e=>setCampus(e.target.value)}>
              <option value="">All Campuses</option>
              {[...new Set(payments.map(p=>p.campus||""))].filter(Boolean).map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            <select className="form-select" style={{width:160}} value={filterMode} onChange={e=>setFilterMode(e.target.value)}>
              <option value="">All Modes</option>
              <option value="Annual">Annual</option>
              <option value="Semestral">Semestral</option>
              <option value="Installment">Installment</option>
            </select>
            <button className="btn btn-outline-primary" onClick={exportMethodCSV}>Export Method CSV</button>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Method</th>
                <th className="text-end">Amount (₱)</th>
                <th className="text-end">% Share</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(byMethod).length === 0 ? (
                <tr><td colSpan="3" className="text-center text-muted">No collections for selected period.</td></tr>
              ) : (
                Object.entries(byMethod).map(([method, amt]) => {
                  const pct = totalCollection ? (Number(amt) / totalCollection * 100) : 0;
                  return (
                    <tr key={method}>
                      <td>{method}</td>
                      <td className="text-end">₱{Number(amt).toLocaleString()}</td>
                      <td className="text-end">{pct.toFixed(1)}%</td>
                    </tr>
                  );
                })
              )}
              <tr className="fw-bold bg-light">
                <td className="text-end">Total</td>
                <td className="text-end">₱{totalCollection.toLocaleString()}</td>
                <td className="text-end">100%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <h6 className="mb-2">Collections by Campus</h6>
          <div className="table-responsive">
            <table className="table table-sm table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>Campus</th>
                  <th className="text-end">Amount (₱)</th>
                  <th className="text-end">% Share</th>
                </tr>
              </thead>
              <tbody>
                {campusStats.length === 0 ? (
                  <tr><td colSpan="3" className="text-center text-muted">No campus data for selected period.</td></tr>
                ) : (
                  campusStats.map(r => {
                    const pct = totalCollection ? (Number(r.total) / totalCollection * 100) : 0;
                    return (
                      <tr key={r.campus}>
                        <td>{r.campus}</td>
                        <td className="text-end">₱{Number(r.total).toLocaleString()}</td>
                        <td className="text-end">{pct.toFixed(1)}%</td>
                      </tr>
                    );
                  })
                )}
                <tr className="fw-bold bg-light">
                  <td className="text-end">Total</td>
                  <td className="text-end">₱{totalCollection.toLocaleString()}</td>
                  <td className="text-end">100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
